'use client';

import { useMemo } from 'react';

import type { EntitySelectorOption } from '@paction-2.0/ui';
import { EntitySelector } from '@paction-2.0/ui/client';

export type BreedingDogOption = {
  id: number;
  name: string | null;
  sex?: 'male' | 'female' | null;
  imageUrl?: string | null;
};

export type ParentSelectorProps = {
  /**
   * Label for the selector (e.g., "Dam", "Sire", "Mother", "Father")
   */
  label: string;
  /**
   * The currently selected breeding dog ID
   */
  value: number | null;
  /**
   * List of breeding dogs to choose from
   */
  options: BreedingDogOption[];
  /**
   * Called when selection changes
   */
  onChange: (id: number | null) => void;
  /**
   * Placeholder text when nothing is selected
   */
  placeholder?: string;
  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Whether options are loading
   */
  loading?: boolean;
  /**
   * Optional callback to create a new breeding dog
   */
  onCreateClick?: () => void;
  /**
   * Whether to show the create placeholder tile
   */
  showCreatePlaceholder?: boolean;
  /**
   * Label for the create placeholder tile
   */
  createPlaceholderLabel?: string;
  /**
   * Label for the create option in the dropdown
   */
  createLabel?: string;
  /**
   * Helper text shown below the selector
   */
  helperText?: string;
};

type DogOptionMeta = {
  sex?: 'male' | 'female' | null;
  imageUrl?: string | null;
};

/**
 * ParentSelector - Dropdown for selecting a breeding dog (dam or sire).
 *
 * Used in waitlist and litter forms to select parent dogs.
 * Shows a searchable dropdown with dog names and a tile preview when selected.
 */
export function ParentSelector({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  required = false,
  error,
  loading = false,
  onCreateClick,
  showCreatePlaceholder = false,
  createPlaceholderLabel,
  createLabel = '+ Add new',
  helperText
}: ParentSelectorProps) {
  const selectorOptions = useMemo(
    (): EntitySelectorOption<DogOptionMeta>[] =>
      options.map((dog) => ({
        id: dog.id,
        label: dog.name || 'Unnamed',
        meta: {
          sex: dog.sex,
          imageUrl: dog.imageUrl
        }
      })),
    [options]
  );

  const handleChange = (option: EntitySelectorOption<DogOptionMeta> | null) => {
    onChange(option ? Number(option.id) : null);
  };

  return (
    <EntitySelector<DogOptionMeta>
      label={label}
      value={value}
      options={selectorOptions}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      error={error}
      helperText={helperText}
      loading={loading}
      loadingText="Loading..."
      onCreateClick={onCreateClick}
      createLabel={createLabel}
      showCreatePlaceholder={showCreatePlaceholder}
      createPlaceholderLabel={createPlaceholderLabel}
      openOnFocus={false}
      renderPreview={(option) => ({
        itemType: 'breedingDog',
        imageUrl: option.meta?.imageUrl,
        title: option.label,
        meta: option.meta?.sex
          ? option.meta.sex === 'female'
            ? 'Female'
            : 'Male'
          : undefined
      })}
    />
  );
}
